import IconSearch from "@simble/shared/assets/icons/search-ico.svg?react";
import useCarSearch from "@simble/shared/hooks/use-car-search";
import { usePartnerStatistics } from "@simble/shared/hooks/use-partner-statistics";
import Button from "@simble/ui/button";
import GRZMaskInput from "@simble/ui/grz-mask-input";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import React, { useCallback, useEffect, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { CarConfirmationDrawer } from "@/components/car-confirmation-drawer";
import Header from "@/components/header";
import PageLayout from "@/layouts/page-layout";
import { store } from "@/store/local-store";

export const Route = createFileRoute("/empty-registration-data")({
  component: EmptyRegistrationData,
});

const MIN_GRZ_LENGTH = 8;

const normalizePhone = (phone: string | undefined): string => {
  if (!phone) {
    return "";
  }
  return phone.startsWith("+7") ? phone.slice(2) : phone;
};

function EmptyRegistrationData() {
  const grzInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const partnerIdForStatistics = store.partnerId.peek() ?? null;
  usePartnerStatistics(partnerIdForStatistics);

  const methods = useForm({
    defaultValues: {
      grz: store.grz.peek() ?? "",
      name: store.fio.peek() ?? "",
      dateOfBirth: (store.dateOfBirth.peek() ?? "").replace(/\./g, ""),
      licenseNumber: (store.passport.peek() ?? "").replace(/\s/g, ""),
      licenseIssueDate: (store.passportDate.peek() ?? "").replace(/\./g, ""),
      experienceStartDate: (store.experienceStartDate.peek() ?? "").replace(/\./g, ""),
      phone: normalizePhone(store.phoneNum.peek()),
    },
  });

  const { watch } = methods;
  const grzValue = watch("grz") || "";

  const [grzError, setGrzError] = React.useState<string | null>(null);
  const [wasSearched, setWasSearched] = React.useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);

  const {
    data: carDataByGrz,
    refetch: refetchGrz,
    isFetching: isFetchingGrz,
    isFetched: isFetchedGrz,
    error: errorGrz,
  } = useCarSearch("grz", grzValue, "ru", { enabled: false });

  useEffect(() => {
    if (!wasSearched || isFetchingGrz) {
      return;
    }
    if (errorGrz) {
      setIsDrawerOpen(true);
      return;
    }
    if (carDataByGrz) {
      setIsDrawerOpen(true);
      return;
    }
    if (isFetchedGrz && !carDataByGrz) {
      setIsDrawerOpen(true);
    }
  }, [wasSearched, isFetchingGrz, carDataByGrz, errorGrz, isFetchedGrz]);

  const handleGrzChange = useCallback(
    (value: string) => {
      if (grzError && value?.length >= MIN_GRZ_LENGTH) {
        setGrzError(null);
      }
    },
    [grzError]
  );

  React.useLayoutEffect(() => {
    const grz = store.grz.peek() ?? "";
    const fio = store.fio.peek() ?? "";
    if (grz && fio) {
      navigate({ to: "/registration-data" });
    }
  }, [navigate]);

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSearchByGrzClick = useCallback(() => {
    if (!grzValue || grzValue.length < MIN_GRZ_LENGTH) {
      setGrzError("Заполните поле полностью");
      return;
    }
    setGrzError(null);
    setWasSearched(true);
    refetchGrz();
  }, [grzValue, refetchGrz]);

  const handleCarConfirm = useCallback(() => {
    methods.reset({ ...methods.getValues(), grz: store.grz.peek() });
    setGrzError(null);
    setWasSearched(false);
    navigate({ to: "/registration-data" });
  }, [methods, navigate]);

  const searchButtonContent = isFetchingGrz ? (
    <div className="box-border size-5 shrink-0 animate-spin rounded-full border-2 border-white/32 border-t-white border-solid" />
  ) : (
    <IconSearch className="size-5 min-w-5" />
  );

  return (
    <PageLayout className="flex flex-col items-center" header={<Header />}>
      <h1 className="headline2 mt-10 px-5 text-[44px] md:px-8">Данные для оформления</h1>
      <div className="mt-5 mb-32 flex w-full max-w-150 flex-col justify-between rounded-4xl bg-[#FAFAFA] py-10">
        <div className="px-6 md:px-10">
          <p className="headline5 text-[22px] text-primary-headline">Автомобиль</p>
          <FormProvider {...methods}>
            <form
              className="mt-5 flex h-full items-center gap-5"
              onSubmit={(e) => {
                e.preventDefault();
                handleSearchByGrzClick();
              }}
            >
              <GRZMaskInput
                className="h-[56px] w-full rounded-xl bg-white"
                control={methods.control}
                error={grzError}
                label="Госномер"
                name="grz"
                onChange={handleGrzChange}
                ref={grzInputRef}
              />
              <Button className="hidden h-[56px] w-[156px] bg-[#4469C6] md:block" disabled={isFetchingGrz} isLoading={isFetchingGrz} type="submit">
                Найти авто
              </Button>
              <Button
                className="block h-[56px] min-w-[56px] bg-[#4469C6] md:hidden"
                disabled={isFetchingGrz}
                leftIcon={searchButtonContent}
                type="submit"
              />
            </form>
          </FormProvider>
        </div>

        <figure className="my-7 h-px w-full bg-divider-thin" />

        <div className="px-6 md:px-10">
          <p className="headline5 text-[22px] text-tertiary">Кого вписать в страховку?</p>
          <p className="body-regular mt-2 text-[16px] text-tertiary">
            Потребуются данные водителя для страхования: ФИО, сведения о водительском удостоверении и стаж
          </p>
        </div>

        <figure className="my-7 h-px w-full bg-divider-thin" />

        <div className="px-6 md:px-10">
          <p className="headline5 text-[22px] text-tertiary">Контактные данные клиента</p>
          <p className="body-regular mt-2 text-[16px] text-tertiary">Номер телефона</p>
          <Button className="mt-5 w-full bg-[#4469C6]/55" disabled={true}>
            Создать профиль
          </Button>
        </div>
      </div>

      <CarConfirmationDrawer
        carData={carDataByGrz}
        error={errorGrz}
        grzValue={grzValue}
        isFetched={isFetchedGrz}
        isFetching={isFetchingGrz}
        onConfirm={handleCarConfirm}
        onOpenChange={setIsDrawerOpen}
        onWasSearchedChange={setWasSearched}
        open={isDrawerOpen}
        wasSearched={wasSearched}
      />
    </PageLayout>
  );
}
